import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet';
import Topbar from '../PulseSurvey/Topbar';
import Breadcrumb from '../../component/common/Breadcrumb';
import Table from '../../component/common/Table';
import SelectMenu from '../../component/common/SelectMenu';
import Pagination from '../../component/common/Pagination/Pagination';
import SearchInput from '../../component/common/Input/SearchInput';
import { PER_PAGE } from '../../utils/constants';
import { getGourmetSocietyOfferUsers } from '../../api/offeringApi';
import { errorToast, getLocalStorageItem } from '../../utils/helper';
import { TableCard } from '../../component/common/designSystem';

const TYPE_LABELS = {
  company: 'Companies',
  b2c: 'B2C Users',
  b2b: 'B2B Users'
};

const maskOfferCode = (offerCode = '') => {
  if (!offerCode || offerCode.length <= 4) return offerCode;
  return `${'*'.repeat(offerCode.length - 4)}${offerCode.slice(-4)}`;
};

const ElloOfferUsers = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const type = searchParams.get('type') || '';

  const [isLoading, setIsLoading] = useState(false);
  const [offerUsers, setOfferUsers] = useState([]);
  const [visibleOfferCodes, setVisibleOfferCodes] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedIsUsedFilter, setSelectedIsUsedFilter] = useState({
    name: 'All Users',
    value: ''
  });

  const userData = getLocalStorageItem('userData') && JSON.parse(getLocalStorageItem('userData'));

  const isUsedFilterOptions = [
    { name: 'All Users', value: '' },
    { name: 'Used', value: 'true' },
    { name: 'Unused', value: 'false' }
  ];

  const pages = [
    {
      name: 'Dashboard',
      href: userData?.slug ? `/${userData?.slug}/dashboard` : '/dashboard',
      current: false
    },
    {
      name: `Gourmet Society – ${TYPE_LABELS[type] || 'Users'}`,
      href: `/ello-offer-users?type=${type}`,
      current: true
    }
  ];

  const toggleOfferCodeVisibility = (userId) => {
    setVisibleOfferCodes((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const fetchOfferUsers = async (page = 1, perPage = selectedPerPage?.value || 10) => {
    setIsLoading(true);
    try {
      const params = {
        page,
        perPage,
        search: debouncedSearchTerm?.trim(),
        isUsed: selectedIsUsedFilter?.value
      };
      if (type) params.type = type;

      const usersResponse = await getGourmetSocietyOfferUsers(null, params);
      const users = Array.isArray(usersResponse?.data) ? usersResponse.data : [];
      const meta = usersResponse?.meta || usersResponse;

      setOfferUsers(users);
      setVisibleOfferCodes({});
      setCurrentPage(Number(meta?.page) || page);
      setTotalCount(Number(meta?.totalRecords) || users.length);
    } catch (error) {
      console.error('Error fetching offer users:', error);
      errorToast(error?.response?.data?.message || 'Failed to fetch offer users');
      setOfferUsers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Re-fetch on filter/page/search changes
  useEffect(() => {
    fetchOfferUsers(currentPage, selectedPerPage?.value || 10);
  }, [currentPage, selectedPerPage, debouncedSearchTerm, selectedIsUsedFilter, type]);

  const tableData = offerUsers.map((user) => {
    const isCodeVisible = !!visibleOfferCodes[user._id];
    return {
      id: user._id,
      name: user.userName || 'N/A',
      email: user.userEmail || 'N/A',
      offerCode: (
        <span className="inline-flex items-center gap-2">
          <span>{isCodeVisible ? user.offerCode : maskOfferCode(user.offerCode)}</span>
          <button
            type="button"
            onClick={() => toggleOfferCodeVisibility(user._id)}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
            aria-label={isCodeVisible ? 'Hide offer code' : 'Show offer code'}
          >
            {isCodeVisible ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
        </span>
      ),
      isUsed: user.isUsed,
      emailSentAt: user.emailSentAt ? new Date(user.emailSentAt).toLocaleString() : 'N/A',
      ctaClickedAt: user.ctaClickedAt ? new Date(user.ctaClickedAt).toLocaleString() : 'N/A',
      redeemedAt: user.redeemedAt ? new Date(user.redeemedAt).toLocaleString() : 'N/A'
    };
  });

  const columns = [
    { title: 'Name', key: 'name', sortable: false, type: 'text', align: 'left' },
    { title: 'Email', key: 'email', sortable: false, type: 'text', align: 'left' },
    { title: 'Offer Code', key: 'offerCode', sortable: false, type: 'text', align: 'left' },
    { title: 'Is Used', key: 'isUsed', sortable: false, type: 'boolean', align: 'center' },
    { title: 'Email Sent At', key: 'emailSentAt', sortable: false, type: 'text', align: 'left' },
    { title: 'CTA Clicked At', key: 'ctaClickedAt', sortable: false, type: 'text', align: 'left' },
    { title: 'Redeemed At', key: 'redeemedAt', sortable: false, type: 'text', align: 'left' }
  ];

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleIsUsedFilterChange = (filterOption) => {
    setSelectedIsUsedFilter(filterOption);
    setCurrentPage(1);
  };

  return (
    <div className="relative lg:px-5 lg:mb-[45px] md:ps-0">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Gourmet Society – {TYPE_LABELS[type] || 'Users'}</title>
      </Helmet>
      <Topbar title={`Gourmet Society – ${TYPE_LABELS[type] || 'Users'}`} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />

      <div className="lg:mt-6 bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {TYPE_LABELS[type] || 'Offer Users'}
          </h2>
        </div>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <SearchInput
              id="elloOfferUserSearch"
              name="elloOfferUserSearch"
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by name or email"
            />
            <div className="w-full sm:w-[160px]">
              <SelectMenu
                menuList={isUsedFilterOptions}
                showLabel={false}
                defaultSelected={selectedIsUsedFilter}
                setSelectedMenu={handleIsUsedFilterChange}
              />
            </div>
          </div>
          <div className="w-full sm:w-[80px]">
            <SelectMenu
              menuList={PER_PAGE}
              showLabel={false}
              defaultSelected={selectedPerPage}
              setSelectedMenu={handlePerPage}
            />
          </div>
        </div>

        <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: selectedPerPage?.value,
            onPageChange: (page) => fetchOfferUsers(page, selectedPerPage?.value),
            onPageSizeChange: (size) =>
              handlePerPage(PER_PAGE.find((o) => o.value === size) || PER_PAGE[0]),
            pageSizeOptions: PER_PAGE.map((o) => o.value)
          }}
        >
          <Table
          columns={columns}
          data={tableData}
          loader={isLoading}
          name="ello_offer_users_table"
          bottomBorder={totalCount > selectedPerPage?.value}
        />
        </TableCard>

        
      </div>
    </div>
  );
};

export default ElloOfferUsers;
